import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaUser, FaEnvelope, FaPhone, FaBuilding, FaCommentDots } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { sendContactEmail } from '../services/emailService';

const ServiceModal = ({ isOpen, onClose, serviceName }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    // Reset form and states when closing
    setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    setErrors({});
    setSubmitSuccess(false);
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Send email using the email service
      const emailResult = await sendContactEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: serviceName || 'Service Inquiry',
        message: `Service Inquiry for: ${serviceName}\n\n${formData.message}`
      });
      
      if (emailResult.success) {
        setSubmitSuccess(true);
        
        // Reset form after 2 seconds and close modal
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        // Handle error
        setErrors({ submit: 'Failed to send message. Please try again.' });
      }
    } catch (error) {
      console.error('Service modal form submission error:', error);
      setErrors({ submit: 'Failed to send message. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-gradient-to-br from-[#22446b]/70 via-[#2d5a8a]/70 to-[#338edc]/70 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-br from-[#22446b] via-[#2d5a8a] to-[#338edc] text-white p-6 rounded-t-2xl">
                {/* Close Button - Fixed z-index and positioning */}
                <button
                  type="button"
                  onClick={handleClose}
                  className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-red-500 hover:rotate-90 transition-all duration-300 z-50 group cursor-pointer"
                  aria-label="Close modal"
                  style={{ zIndex: 9999 }}
                >
                  <FaTimes className="text-lg text-white pointer-events-none" />
                </button>
                
                <div className="relative z-10 pr-8">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs font-semibold mb-2 text-white border border-white/20">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                    {serviceName || 'New Project'}
                  </div>
                  <h2 className="text-2xl font-rajdhani font-bold mb-1 text-white">
                    Start Your Project
                  </h2>
                  <p className="text-white/80 font-opensans text-sm">
                    {serviceName ? `Let's discuss your ${serviceName} project` : "Let's bring your vision to life"}
                  </p>
                </div>
              </div>

              {/* Form */}
              <div className="p-6">
                {submitSuccess ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-rajdhani font-bold text-gray-900 mb-1">
                      Thank You!
                    </h3>
                    <p className="text-gray-600 font-opensans text-sm">
                      We'll get back to you within 24 hours.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name & Email Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Name Field */}
                      <div>
                        <label className="block text-xs font-bold text-gray-800 mb-1.5 font-opensans">
                          Full Name *
                        </label>
                        <div className="relative">
                          <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full pl-9 pr-3 py-2.5 border ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:border-black focus:ring-1 focus:ring-black/20 focus:outline-none transition-all font-opensans text-gray-900 text-sm placeholder:text-gray-400`}
                            placeholder="John Doe"
                          />
                        </div>
                        {errors.name && <p className="text-red-600 text-xs mt-1 font-opensans">{errors.name}</p>}
                      </div>

                      {/* Email Field */}
                      <div>
                        <label className="block text-xs font-bold text-gray-800 mb-1.5 font-opensans">
                          Email Address *
                        </label>
                        <div className="relative">
                          <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full pl-9 pr-3 py-2.5 border ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:border-black focus:ring-1 focus:ring-black/20 focus:outline-none transition-all font-opensans text-gray-900 text-sm placeholder:text-gray-400`}
                            placeholder="john@example.com"
                          />
                        </div>
                        {errors.email && <p className="text-red-600 text-xs mt-1 font-opensans">{errors.email}</p>}
                      </div>
                    </div>

                    {/* Phone & Company Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Phone Field */}
                      <div>
                        <label className="block text-xs font-bold text-gray-800 mb-1.5 font-opensans">
                          Phone Number *
                        </label>
                        <div className="relative">
                          <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={`w-full pl-9 pr-3 py-2.5 border ${errors.phone ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:border-black focus:ring-1 focus:ring-black/20 focus:outline-none transition-all font-opensans text-gray-900 text-sm placeholder:text-gray-400`}
                            placeholder="+91 7483961283"
                          />
                        </div>
                        {errors.phone && <p className="text-red-600 text-xs mt-1 font-opensans">{errors.phone}</p>}
                      </div>

                      {/* Company Field */}
                      <div>
                        <label className="block text-xs font-bold text-gray-800 mb-1.5 font-opensans">
                          Company <span className="text-gray-500 font-normal">(Optional)</span>
                        </label>
                        <div className="relative">
                          <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:border-black focus:ring-1 focus:ring-black/20 focus:outline-none transition-all font-opensans text-gray-900 text-sm placeholder:text-gray-400"
                            placeholder="Your Company"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Message Field */}
                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1.5 font-opensans">
                        Project Details *
                      </label>
                      <div className="relative">
                        <FaCommentDots className="absolute left-3 top-3 text-gray-400 text-sm" />
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="3"
                          className={`w-full pl-9 pr-3 py-2.5 border ${errors.message ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-lg focus:border-black focus:ring-1 focus:ring-black/20 focus:outline-none transition-all resize-none font-opensans text-gray-900 text-sm placeholder:text-gray-400 leading-relaxed`}
                          placeholder="Tell us about your project..."
                        />
                      </div>
                      {errors.message && <p className="text-red-600 text-xs mt-1 font-opensans">{errors.message}</p>}
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="flex-1 py-2.5 px-4 border border-gray-300 text-gray-700 font-rajdhani font-bold text-sm rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 py-2.5 px-4 bg-gradient-to-r from-[#22446b] to-[#338edc] text-white font-rajdhani font-bold text-sm rounded-lg hover:from-[#338edc] hover:to-[#22446b] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Submitting...
                          </span>
                        ) : (
                          'Submit Request'
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ServiceModal;
